import useExperiment from 'hooks/common/use-experiment';
import { ShopifyImage } from 'interfaces/product';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { ABCD_TEST_CONFIG, MOOD_IMAGE_CONFIG } from './config';

interface UseTest7443Props {
  originalImages: ShopifyImage[];
  productTitle: string;
}

interface UseTest7443Return {
  images: ShopifyImage[];
}

/**
 * Custom hook for PT-7443 A/B test: Mood & USP image test
 * @see https://petsdeli.atlassian.net/browse/PT-7443
 *
 * Handles the injection of mood/USP images at specified positions based on A/B test variant.
 *
 * Test 1 (AB): Control vs Mood image
 * Test 2 (ABCD): Control vs Mood vs USP vs Both
 */
export const useTest7443 = ({
  originalImages,
  productTitle,
}: UseTest7443Props): UseTest7443Return => {
  const router = useRouter();

  const { variant: variantTest1, loading: loadingTest1 } = useExperiment({
    baseEntryId: '3cQVERZu4IKjfiaW3aeBo4',
  });

  const { variant: variantTest2, loading: loadingTest2 } = useExperiment({
    baseEntryId: '4hn8SgpeaBsRUPOkwaKDVH',
  });

  const images = useMemo(() => {
    // Get current product handle from router, removing query parameters
    const currentPath = router.asPath.split('?')[0];

    // Check if either test is still loading
    if (loadingTest1 || loadingTest2) return originalImages;

    const modifiedImages = [...originalImages];

    // Test 1: AB Test (Control vs Mood image)
    if (variantTest1 === 1) {
      const moodConfig = MOOD_IMAGE_CONFIG[currentPath];
      if (moodConfig) {
        const moodImage: ShopifyImage = {
          id: `mood-image-${moodConfig.imageName}`,
          originalSrc: `/images/pt-7443/${moodConfig.imageName}`,
          altText: `Mood image for ${productTitle}`,
        };

        const insertIndex = Math.min(
          moodConfig.position - 1,
          modifiedImages.length
        );
        modifiedImages.splice(insertIndex, 0, moodImage);
      }
    }

    // Test 2: ABCD Test (Control vs Mood vs USP vs Both)
    const abcdConfig = ABCD_TEST_CONFIG[currentPath];
    if (abcdConfig && variantTest2 !== undefined) {
      const imagesToAdd: Array<{ image: ShopifyImage; position: number }> = [];

      // Variant 1: Add mood image at position 4
      if (variantTest2 === 1) {
        imagesToAdd.push({
          image: {
            id: `mood-image-test2-${abcdConfig.moodImage}`,
            originalSrc: `/images/pt-7443/${abcdConfig.moodImage}`,
            altText: `Mood image for ${productTitle}`,
          },
          position: abcdConfig.moodPosition,
        });
      }

      // Variant 2: Add USP image at position 4
      else if (variantTest2 === 2) {
        imagesToAdd.push({
          image: {
            id: `usp-image-test2-${abcdConfig.uspImage}`,
            originalSrc: `/images/pt-7443/${abcdConfig.uspImage}`,
            altText: `USP image for ${productTitle}`,
          },
          position: abcdConfig.uspPosition,
        });
      }

      // Variant 3: Add mood image at position 4 AND USP image at position 5
      else if (variantTest2 === 3) {
        imagesToAdd.push(
          {
            image: {
              id: `mood-image-test2-${abcdConfig.moodImage}`,
              originalSrc: `/images/pt-7443/${abcdConfig.moodImage}`,
              altText: `Mood image for ${productTitle}`,
            },
            position: abcdConfig.moodPosition,
          },
          {
            image: {
              id: `usp-image-test2-${abcdConfig.uspImage}`,
              originalSrc: `/images/pt-7443/${abcdConfig.uspImage}`,
              altText: `USP image for ${productTitle}`,
            },
            position: 5, // hardcoded to position 5 for Variant 3
          }
        );
      }

      // Insert images in ascending order of position for consecutive positions
      imagesToAdd
        .sort((a, b) => a.position - b.position)
        .forEach(({ image, position }) => {
          const insertIndex = Math.min(position - 1, modifiedImages.length);
          modifiedImages.splice(insertIndex, 0, image);
        });
    }

    return modifiedImages;
  }, [
    loadingTest1,
    loadingTest2,
    originalImages,
    productTitle,
    router.asPath,
    variantTest1,
    variantTest2,
  ]);

  return {
    images,
  };
};

export default useTest7443;
